import CaptchaInitializer from "../../../Scalesoft.Shared.FrontEnd/Scripts/Captcha/CaptchaInitializer";
import CaptchaTokenManager from "../../../Scalesoft.Shared.FrontEnd/Scripts/Captcha/CaptchaTokenManager";
import ErrorHandler from "../../../Scalesoft.Shared.FrontEnd/Scripts/Error-Handler/ErrorHandler";
import GuiCommonShared from "../../../Scalesoft.Shared.FrontEnd/Scripts/GuiCommonShared";
import CompareFunctions from "../CompareFunctions";
import ContactList from "../Contacts/ContactList";
import InputEqualityChecker from "../InputEqualityChecker";
import InputFormat from "../InputFormat";
import NamesManager from "../NamesManager";
import PasswordControl from "../PasswordControl";
import RecapTableBuilder, {IRecapRowItem} from "../RecapTableBuilder";
import FormVerifierAndMarker from "../RegistrationVerificationShared/FormVerifierAndMarker";
import VerificationTypeToggler from "../RegistrationVerificationShared/VerificationTypeToggler";
import TabNavigation from "../TabNavigation";
import ApiClient from "./ApiClient";
import RegistrationHelp from "./RegistrationHelp";

declare const formIsInvalid: boolean;
declare const thirdPartyRegistration: boolean;

jq(() => {
    const registerForm = new RegisterForm(
        window.localization,
    );
    registerForm.init();

    const registrationHelp = new RegistrationHelp();
    registrationHelp.init();
});

class RegisterForm {
    private readonly registerFormEl = jq("#user-register") as JQuery<HTMLFormElement>;

    private readonly formatInput: InputFormat;
    private readonly contactList: ContactList;
    private readonly namesManager: NamesManager;
    private readonly inputEqualityChecker: InputEqualityChecker;

    private readonly contactDialogsContainer: JQuery;
    private readonly credentialsTabContent: JQuery;

    private readonly mLocalization: Localization;

    private readonly registrationTabs: JQuery;
    private mVerificationTypeToggler: VerificationTypeToggler;
    private mFormVerifierAndMarker: FormVerifierAndMarker;
    private readonly captchaInitializer: CaptchaInitializer;
    private readonly apiClient: ApiClient;
    private readonly recapTableBuilder: RecapTableBuilder;

    constructor(
        localization: Localization,
    ) {
        this.mLocalization = localization;
        this.contactDialogsContainer = jq(".contact-dialogs");
        this.credentialsTabContent = jq("#credentials-info");
        this.registrationTabs = this.registerFormEl.find(".nav-link");
        this.formatInput = new InputFormat(this.mLocalization);
        this.apiClient = new ApiClient();
        const guiCommon = new GuiCommonShared(localization);
        const captchaTokenManager = new CaptchaTokenManager();
        captchaTokenManager.init(jq);
        this.captchaInitializer = new CaptchaInitializer(captchaTokenManager);
        this.mVerificationTypeToggler = new VerificationTypeToggler();
        this.contactList = new ContactList(
            this.registerFormEl,
            this.contactDialogsContainer,
            this.captchaInitializer,
            new ErrorHandler(localization),
            guiCommon,
            localization,
            "User",
            (cardEl) => {
                this.mVerificationTypeToggler.initialiseOnCard(cardEl);
            },
        );
        this.mFormVerifierAndMarker = new FormVerifierAndMarker(this.registerFormEl, this.registrationTabs, this.contactList);

        this.namesManager = new NamesManager("add-name-btn", "remove-name-input-btn", localization);

        this.inputEqualityChecker = new InputEqualityChecker(guiCommon);
        this.recapTableBuilder = new RecapTableBuilder(
            localization,
            async (verifyCheckbox) => {
                if (verifyCheckbox.is(":checked")) {
                    const verifyLabel = await this.mLocalization.translateAsync("mark-for-verification", "UserInfoViewModel");
                    return `<span class=\"verified-data-mark\"><i class=\"fas fa-lg checkbox-icon fa-shield-alt fa-fw text-success\"></i>${verifyLabel}</span></td>`;
                }
                return "";
            },
        );
    }

    public init(): void {
        if (TabNavigation !== undefined) {
            const tabNavigation = new TabNavigation();
            tabNavigation.initRegistrationNavigation((formFragment) => this.mFormVerifierAndMarker.validateFormFragment(formFragment));
        }

        jq(".verification-checkbox label").tooltip();

        this.attachSubmitEventListener();
        // form input toggle moved to FormInputToggler
        this.mFormVerifierAndMarker.attachTabSwitchEvents();
        this.attachPersonalNumberSanitizerListener();
        this.initSubmitFormBtn();

        if (PasswordControl !== undefined) {
            const passwordStrengthEstimator = new PasswordControl(this.registerFormEl);
            passwordStrengthEstimator.init();
        }

        if (thirdPartyRegistration) {
            this.populateUserPassword();
        }

        // this.formatInput.formatPersonalNumber(this.credentialsTabContent);

        this.registerFormEl.on("keydown", (event: JQuery.KeyDownEvent) => {
            if (event.key === "Enter") {
                const activeTab = this.registrationTabs.filter(".active");
                const activeTabContentJq = jq(activeTab.attr("data-bs-target"));
                const nextBtn = activeTabContentJq.find(".finish-registration-btn, .next-step-btn");
                nextBtn.trigger("click");
                event.preventDefault();
                return false;
            }
        });

        this.registerFormEl.find(".modal").on("keydown", (event: JQuery.KeyDownEvent) => {
            if (event.key === "Enter") {
                jq(event.currentTarget).find(".submit-modal-form-btn").trigger("click");
            }
        });

        const insuranceNumberInputSelector = ".insurance-number-input-selector";
        const personalIdInputSelector = ".personal-id-input-selector";
        const notificationContainer = jq(".equality-alert-container");
        this.mLocalization.translateAsync("insurance-number-personal-id-inequality", "UserInput")
            .then((inequalityMessageTranslation) => {
                this.inputEqualityChecker.elementsToCheckAdd(
                    insuranceNumberInputSelector,
                    personalIdInputSelector,
                    inequalityMessageTranslation.result.value,
                    notificationContainer,
                );

                this.namesManager.init();
            });

        const birthDateInputSelector = ".birth-date-input[type=hidden]";
        this.mLocalization.translateAsync("birth-date-personal-id-inequality", "UserInput")
            .then((inequalityMessageTranslation) => {
                this.inputEqualityChecker.elementsToCheckAdd(
                    birthDateInputSelector,
                    personalIdInputSelector,
                    inequalityMessageTranslation.result.value,
                    jq(".birthdate-equality-alert-container"),
                    CompareFunctions.birthdayCompareToPersonalId(),
                );
            });

        if (formIsInvalid) {
            this.mFormVerifierAndMarker.markAllTabs();
        }

        this.mVerificationTypeToggler.init();
    }

    private attachSubmitEventListener(): void {
        this.captchaContainerTabTracking();

        this.registerFormEl.on("submit", (event) => {
            this.registerFormEl.find(":disabled").removeAttr("disabled").addClass("readonly");
            event.preventDefault();
            this.captchaInitializer.initForForm(jq, this.registerFormEl, "registration");
        });
    }

    private captchaContainerTabTracking(): void {
        this.registrationTabs.on("hide.bs.tab", (event) => {
            const lastActiveTabJq = jq(event.target);
            const lastTabContentJq = jq(lastActiveTabJq.attr("data-bs-target"));

            const registrationFinishButtonOnPreviousTabJq = lastTabContentJq.find(".finish-registration-btn");

            if (registrationFinishButtonOnPreviousTabJq.length) {
                this.captchaInitializer.cleanupContainer(lastTabContentJq);
            }
        });
        this.registrationTabs.on("show.bs.tab", (event) => {
            const activeTabJq = jq(event.target);
            const activeTabContentJq = jq(activeTabJq.attr("data-bs-target"));

            const registrationFinishButtonOnCurrentTabJq = activeTabContentJq.find(".finish-registration-btn");

            if (registrationFinishButtonOnCurrentTabJq.length) {
                this.captchaInitializer.prepareContainer(jq, activeTabContentJq);
            }
        });
    }

    private initSubmitFormBtn(): void {

        this.registerFormEl.find(".finish-registration-btn").on("click", (event) => {
            const currentValidationFieldsIgnoreSelector = this.mFormVerifierAndMarker.getFormValidationIgnoreSelector(this.registerFormEl);
            this.mFormVerifierAndMarker.ignoreNoFields(this.registerFormEl);

            const activeTab = this.registrationTabs.filter(".active");
            const activeTabContent = jq(activeTab.attr("data-bs-target"));
            const isFragmentValid = this.mFormVerifierAndMarker.validateFormFragment(activeTabContent);
            this.mFormVerifierAndMarker.markTab(activeTab, isFragmentValid);

            const uncheckedTabs = this.registrationTabs.filter(":not(.valid-tab):not(.invalid-tab)");
            let isFormValid = true;
            this.registrationTabs.each((index, element) => {
                const elementJq = jq(element);
                const tabContent = jq(elementJq.attr("data-bs-target"));

                const isTabValid = this.mFormVerifierAndMarker.validateFormFragment(tabContent);
                if (!(elementJq.hasClass("valid-tab") || elementJq.hasClass("invalid-tab"))) {
                    this.mFormVerifierAndMarker.markTab(elementJq, isTabValid);
                }

                isFormValid = isFormValid && isTabValid;
            });

            if (!isFormValid) {
                event.preventDefault();

                this.mFormVerifierAndMarker.setFormValidationIgnoreSelector(this.registerFormEl, currentValidationFieldsIgnoreSelector);
                const errorModal = this.registerFormEl.find(".error-modal");
                errorModal.modal("show");

                const recheckRegistrationBtn = errorModal.find(".check-registration-btn");
                recheckRegistrationBtn.off("click");
                recheckRegistrationBtn.on("click", () => {
                    // Switch tab to first invalid tab (invalid or unchecked)
                    this.registrationTabs.filter(":not(.valid-tab)").first().tab("show");
                    errorModal.modal("hide");
                });
            } else {
                const recapDialog = jq("#recap-dialog");
                if (recapDialog.length > 0) {
                    recapDialog.modal("show");
                    const dialogBody = recapDialog.find(".modal-body");
                    dialogBody.empty();
                    const registeredUserMessage = recapDialog.attr("data-registered-user-message");
                    const verifiedUserMessage = recapDialog.attr("data-verified-user-message");
                    const recaptTable = this.getRecapHtml(
                        registeredUserMessage,
                        verifiedUserMessage,
                        recapDialog,
                    );

                    dialogBody.append(recaptTable);
                    event.preventDefault();
                }
                this.updateContactsIndexes();
            }
        });
    }

    private getRecapHtml(
        registeredUserMessage: string,
        verifiedUserMessage: string,
        recapDialog: JQuery,
    ): JQuery<HTMLElement> {
        const inputCards = this.registerFormEl.find(".input-container");
        let userWillBeVerified = true;
        const recapRowItems: IRecapRowItem[] = inputCards.map((index, element) => {
            const elementJq = jq(element);

            const verifyCheckboxElement = elementJq.find(".verification-checkbox input[type=\"checkbox\"]");
            if (verifyCheckboxElement.length === 0) {
                return;
            }

            let labelElement: JQuery<Element>;
            if (elementJq.hasClass("last-names") || elementJq.hasClass("first-names")) {
                labelElement = elementJq.prev("label");
            } else {
                labelElement = elementJq.find("label").filter((index1, element1) => {
                    // Remove verification checkbox labels from set
                    return jq(element1).parents(".verification-checkbox").length === 0;
                });
            }

            // Select only inputs or select with verified value - not checkboxes and hidden inputs)
            const valueInputs = elementJq.find("input:not([type=\"checkbox\"]):not([type=\"hidden\"]), select");

            if (valueInputs.filter(".is-required-default").length > 0) {
                userWillBeVerified = userWillBeVerified && verifyCheckboxElement.is(":checked");
            }

            const separatorElement = elementJq.find(".input-separator");
            let separator = " ";
            if (separatorElement.length > 0) {
                separator = separatorElement.text();
            }
            const label = labelElement.map((index1, domElement) => {
                return jq(domElement).html();
            }).get().join(` ${separator} `);
            return {
                separator,
                valueInputs,
                label,
                verifyCheckboxElement,
            } as IRecapRowItem;

        }).get();
        const tablePromise = this.recapTableBuilder.buildRecapTable(recapRowItems);
        const tableContainer = jq("<div></div>");

        tablePromise.then((table) => {
            tableContainer.append(table);
            const roleContainer = recapDialog.find(".registered-user-role-container");
            if (userWillBeVerified) {
                roleContainer.html(`<div class="pb-1">${verifiedUserMessage}</div>`);
            } else {
                roleContainer.html(`<div class="pb-1">${registeredUserMessage}</div>`);
            }
        });

        return tableContainer;
    }

    private updateContactsIndexes(): void {
        this.registerFormEl.find(".postal-card-template").remove();
        this.registerFormEl.find(".postalAddress-row > .tile:not(.add-contact) .contact").each((index, element) => {
            const contactIndex = index.toString(10);
            jq(element).find("input").each((inputIndex, inputElement) => {
                const inputElJq = jq(inputElement);
                const newName = inputElJq.attr("name").replace(/\d/g, contactIndex);
                inputElJq.attr("name", newName);

                const inputId = inputElJq.attr("id");
                if (inputId) {
                    const newId = inputId.replace(/\d/g, contactIndex);
                    inputElJq.attr("id", newId);
                }
            });
        });
    }

    private attachPersonalNumberSanitizerListener(): void {
        jq(".personal-id-input-selector").on("keyup", (event) => {
            const element = event.target as HTMLInputElement;
            element.value = element.value.replace("/", "");
        });
    }

    private populateUserPassword(): void {
        const passwordGenerateErrorAlertJq = jq(".password-generate-error-alert-selector");
        const passwordRequirementsAlertJq = jq(".password-requirements-alert-selector");
        const passwordGeneratedAlertJq = jq(".password-generated-alert-selector");
        const passwordEnterManuallyButtonJq = jq(".enter-pass-manually-btn-selector");
        const passwordGenerateButtonJq = jq(".generate-password-btn-selector");
        const passwordInputJq = jq(".password-input-selector");
        const passwordConfirmInputJq = jq(".password-confirm-input-selector");

        passwordInputJq.prop("readonly", true);
        passwordConfirmInputJq.prop("readonly", true);

        const sendRequestAndPopulatePassword = () => {
            passwordGenerateErrorAlertJq.addClass("d-none");
            const passAjax = this.apiClient.getGeneratedUserPassword();
            passAjax.done((response) => {
                passwordInputJq.val(response);
                passwordInputJq
                    .trigger("input") // trigger password strength estimator
                    .trigger("change")
                    .valid();
                passwordConfirmInputJq.val(response);
            });
            passAjax.fail(() => {
                passwordGenerateErrorAlertJq.removeClass("d-none");
                passwordGenerateButtonJq.removeClass("d-none");
            });
        };

        sendRequestAndPopulatePassword();
        passwordGenerateButtonJq.on("click", sendRequestAndPopulatePassword);

        passwordEnterManuallyButtonJq.on("click", () => {
            passwordInputJq.val("");
            passwordConfirmInputJq.val("");
            passwordInputJq.trigger("input"); // trigger password strength estimator

            passwordRequirementsAlertJq.removeClass("d-none");
            passwordGeneratedAlertJq.addClass("d-none");
            passwordEnterManuallyButtonJq.addClass("d-none");
            passwordGenerateButtonJq.removeClass("d-none");
            passwordInputJq.prop("readonly", false);
            passwordConfirmInputJq.prop("readonly", false);
        });
    }
}
